import { api, ApiError } from "../api/client";

/** How long a page waits for a document before handing the wait back to the card's own poll. */
export const POLICY_GENERATION_WAIT_MS = 20 * 60 * 1000;

/** What a running set generation reports: how many are written and which one is being written now. */
export interface JobProgress { done: number; total: number; current?: string | null }

/** The stages one document moves through on the worker (backend lib/generationProgress.ts). */
export type GenerationStage = "preparing" | "writing" | "checking" | "revising" | "formatting" | "saving";

/** What a single document's job reports while it runs. */
export interface DocumentProgress {
  kind: "document";
  stage: GenerationStage;
  /** Which pass of a long answer is being written. */
  pass?: number;
  /** When the worker started on it (ms since epoch). */
  startedAt: number;
  /** How long a document like this usually takes, end to end (ms). */
  estimateMs: number;
}

export type AnyJobProgress = JobProgress | DocumentProgress;

export const isDocumentProgress = (p: AnyJobProgress): p is DocumentProgress => (p as DocumentProgress).kind === "document";

/** Where the job is: waiting on the queue (since `queuedAt`) or being written. */
export interface JobStatus { status: "queued" | "running"; queuedAt?: number }

type JobState<R> =
  | { status: "queued" | "running"; progress?: AnyJobProgress; queuedAt?: number }
  | ({ status: "done" } & R)
  | { status: "failed"; httpStatus?: number; error?: string; message?: string };

interface PollOptions {
  onProgress?: (p: AnyJobProgress) => void;
  onStatus?: (s: JobStatus) => void;
  waitMs?: number;
}

/**
 * Starts a document generation and waits for it. The API queues the writing on
 * the worker (a full document takes minutes) and answers with a job id; this
 * polls the job and resolves with the body the generate call used to return. A
 * refusal (spend cap, no law in scope, parent policy missing) rejects with an
 * ApiError carrying the status and message the API gave.
 */
export async function runDocumentJob<R extends object>(
  generatePath: string,
  body: unknown = {},
  options: PollOptions & { onJobId?: (jobId: string) => void } = {},
): Promise<R> {
  const { jobId } = await api.post<{ jobId: string }>(generatePath, body);
  options.onJobId?.(jobId);
  return pollDocumentJob<R>(jobId, options);
}

/** Waits for a queued document job already started (by this page or before a reload). */
export async function pollDocumentJob<R extends object>(
  jobId: string,
  options: PollOptions = {},
): Promise<R> {
  const deadline = Date.now() + (options.waitMs ?? POLICY_GENERATION_WAIT_MS);
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 3000));
    let state: JobState<R>;
    try {
      state = await api.get<JobState<R>>(`/templates/generation-jobs/${encodeURIComponent(jobId)}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) throw e;
      continue; // transient — keep waiting
    }
    if (state.status === "running" || state.status === "queued") {
      options.onStatus?.({ status: state.status, queuedAt: state.queuedAt });
      if (state.progress) options.onProgress?.(state.progress);
    }
    if (state.status === "done") {
      const { status: _status, ...body } = state;
      return body as unknown as R;
    }
    if (state.status === "failed") {
      throw new ApiError(state.httpStatus ?? 500, { error: state.error ?? "generation_failed", message: state.message });
    }
  }
  throw new Error("The document is taking longer than usual. It will appear here when it is ready.");
}

/** A policy or data protection document: resolves with `{ document, content }`. */
export function generatePolicyDocument<D>(
  templateId: string,
  options: PollOptions & { onJobId?: (jobId: string) => void } = {},
): Promise<{ document: D; content: string }> {
  return runDocumentJob<{ document: D; content: string }>(`/templates/${templateId}/generate`, {}, options);
}

/** Rewrites one level-2 section of the current document from the reader's feedback. */
export function rewritePolicySection<D>(templateId: string, heading: string, feedback: string): Promise<{ document: D; content: string }> {
  return runDocumentJob<{ document: D; content: string }>(`/templates/${templateId}/sections/rewrite`, { heading, feedback });
}

export interface SetGenerationResult { templateId: string; ok: boolean; versionNumber?: number; error?: string }
